// Lesson framing shown around the live question flow.
//
// The server only stores each question's type, payload and order — it has no
// concept of a "unit", an "instructor" or a "learning objective". That
// human-facing context lives here, keyed by room code (stable across resets),
// so the projector and the student's phone can frame every question as part of
// a lesson rather than a bare quiz. Rooms with no entry fall back to a generic
// shape so a freshly created room still works.

export const REFLECTION = 'reflection';

const REFLECTION_CHIP = {
  id: REFLECTION,
  label: 'ทบทวน',
  text: 'ทบทวนและสะท้อนการเรียนรู้',
  reflection: true,
};

const LESSONS = {
  PYINIT: {
    unit: 'หน่วยที่ 1',
    title: 'การเตรียมความพร้อมและการเริ่มต้นใช้งานภาษาไพทอน',
    subtitle: 'การติดตั้งและตั้งค่าสภาพแวดล้อม · เครื่องมือพัฒนาโปรแกรม (IDE/Editor)',
    instructor: 'นายกฤตนัย บุญน้อย',
    questionCount: 7,
    objectives: [
      {
        id: 'obj1',
        label: 'จุดประสงค์ 1',
        text: 'บอกขั้นตอนการติดตั้งและตั้งค่าระบบสำหรับการเขียนโปรแกรมภาษาไพทอนได้',
      },
      {
        id: 'obj2',
        label: 'จุดประสงค์ 2',
        text: 'บอกหน้าที่ของเครื่องมือเขียนโค้ด (IDE/Editor) ได้',
      },
    ],
    // question orderIndex (0-based, as sent on the wire) -> objective id / REFLECTION
    questionObjectives: {
      0: REFLECTION, // have you ever installed software yourself (warm-up)
      1: 'obj1',     // the official Python download site
      2: 'obj1',     // tick Add python.exe to PATH
      3: 'obj2',     // the IDE this lesson uses
      4: 'obj1',     // order the Python + Thonny install steps
      5: 'obj2',     // the run order: Thonny -> Interpreter -> output
      6: REFLECTION, // what you took away from the lesson
    },
  },

  PYVAR: {
    unit: 'หน่วยที่ 2',
    title: 'การกำหนดตัวแปรและการเลือกใช้งานชนิดข้อมูลประเภทตัวเลข',
    subtitle: 'กฎการตั้งชื่อตัวแปร · การเลือกใช้ int และ float ให้เหมาะกับข้อมูล',
    instructor: 'นายกฤตนัย บุญน้อย',
    questionCount: 5,
    objectives: [
      {
        id: 'obj1',
        label: 'จุดประสงค์ 1',
        text: 'บอกความหมายของตัวแปรและกำหนดค่าให้ตัวแปรตามกฎการตั้งชื่อได้',
      },
      {
        id: 'obj2',
        label: 'จุดประสงค์ 2',
        text: 'บอกชนิดข้อมูลจำนวนเต็ม (int) และจำนวนทศนิยม (float) ให้เหมาะสมกับข้อมูลที่กำหนดให้ได้',
      },
    ],
    // question orderIndex (0-based, as sent on the wire) -> objective id / REFLECTION
    questionObjectives: {
      0: REFLECTION, // the old warm-up — no longer in the deck (the opening is the slip/OCR slide instead)
      1: 'obj1',     // what score = 25 means
      2: 'obj1',     // which variable name follows the rules
      3: 'obj2',     // int or float for a single case
      4: 'obj2',     // int or float for five items (dropdown)
      5: REFLECTION, // what you took away from the lesson
    },
  },

  PYOP: {
    unit: 'หน่วยที่ 3',
    title: 'ใช้งานตัวดำเนินการเพื่อประมวลผล คำนวณ และจัดการข้อมูลพื้นฐาน',
    subtitle: 'ตัวดำเนินการทางคณิตศาสตร์ · ลำดับความสำคัญ · การกำหนดค่าแบบย่อ',
    instructor: 'นายกฤตนัย บุญน้อย',
    questionCount: 6,
    objectives: [
      {
        id: 'obj1',
        label: 'จุดประสงค์ 1',
        text: 'บอกหน้าที่ของตัวดำเนินการทางคณิตศาสตร์ และเขียนนิพจน์คำนวณให้ได้ผลลัพธ์ตามที่กำหนดได้',
      },
      {
        id: 'obj2',
        label: 'จุดประสงค์ 2',
        text: 'บอกลำดับความสำคัญของตัวดำเนินการ และวิเคราะห์ผลลัพธ์ของนิพจน์ที่มีหลายตัวดำเนินการได้',
      },
    ],
    // question orderIndex (0-based, as sent on the wire) -> objective id / REFLECTION
    questionObjectives: {
      0: REFLECTION, // opening: 1,250 split three ways — where does the remainder go
      1: 'obj1',     // what 1250 // 3 and 1250 % 3 give
      2: 'obj1',     // fill in the operator that reaches the target
      3: 'obj2',     // 10 / 2 * 3 is 15.0
      4: 'obj2',     // order the calculation steps
      5: REFLECTION, // what you took away from the lesson
    },
  },

  // Unit 2 rebuilt for the final session (ADR 0003). `theme` puts the
  // learner's phone in theme A, matching the deck's `meta.theme`.
  PYVAR2: {
    unit: 'หน่วยที่ 2',
    title: 'การกำหนดตัวแปรและการเลือกใช้งานชนิดข้อมูลประเภทตัวเลข',
    subtitle: 'ตัวแปรคือป้ายชื่อที่ผูกกับค่า · นับได้ใช้ int วัดได้ใช้ float',
    instructor: 'นายกฤตนัย บุญน้อย',
    questionCount: 3,
    theme: 'tag',
    objectives: [
      {
        id: 'obj1',
        label: 'จุดประสงค์ 1',
        text: 'บอกความหมายของตัวแปร และกำหนดค่าให้ตัวแปรโดยตั้งชื่อตามกฎการตั้งชื่อได้',
      },
      {
        id: 'obj2',
        label: 'จุดประสงค์ 2',
        text: 'บอกชนิดข้อมูลจำนวนเต็ม (int) และจำนวนทศนิยม (float) ให้เหมาะสมกับลักษณะของข้อมูลที่กำหนดให้ได้',
      },
    ],
    questionObjectives: {
      0: 'obj1', // what score = score + 5 prints
      1: 'obj1', // which name tags Python refuses
      2: 'obj2', // int or float for five pieces of data
    },
  },
};

const GENERIC_LESSON = {
  unit: 'บทเรียน',
  title: 'กิจกรรมในชั้นเรียน',
  subtitle: null,
  instructor: null,
  questionCount: null,
  objectives: [],
  questionObjectives: {},
};

export function getLesson(roomCode) {
  const key = String(roomCode || '').toUpperCase();
  return LESSONS[key] || null;
}

/** Lesson context that is always safe to render, even for an unknown room. */
export function getLessonOrGeneric(roomCode) {
  return getLesson(roomCode) || GENERIC_LESSON;
}

/** The objective (or reflection) chip a given question serves, or null. */
export function getObjectiveForQuestion(roomCode, orderIndex) {
  const lesson = getLesson(roomCode);
  if (!lesson || orderIndex == null) return null;
  const ref = lesson.questionObjectives?.[orderIndex];
  if (!ref) return null;
  if (ref === REFLECTION) return REFLECTION_CHIP;
  return lesson.objectives.find((o) => o.id === ref) || null;
}
